'use strict';

/* global angular */

/**
 * @module TemplateService
 *
 * Template service, responsible for reading the template list and the contained templates.
 *
 * The template service will serve templates from a list of template aliases specified in a Content. It will look
 * for the list first by looking up a content (of the type aceUITemplateListContent) with an alias specified in the
 * TemplateService configuration property 'templateListAlias', or if not configured, fallback to lookup a content under
 * the alias 'aceUI/defaultTemplateList'.
 */
angular.module('atex.aceui.service').factory('TemplateService',
  ['$q', 'ContentService', 'FileDeliveryService', 'AuthorizationService', 'ApplicationConfigurationService',
    function ($q, ContentService, FileDeliveryService, AuthorizationService, ApplicationConfigurationService) {
      var AceUIError = atex.aceui.error,
        templatesRequest, quickCreatorTemplatesRequest;

      function fetchAndCacheTemplates (templateGroup) {
        // Get configuration
        if (templateGroup === 'QuickCreator') {
          return quickCreatorTemplatesRequest || (quickCreatorTemplatesRequest = fetchTemplates(templateGroup))
            .catch(function (error) {
              console.log('ERROR caught');
              quickCreatorTemplatesRequest = undefined;
              return $q.reject(error);
            });
        } else {
          return templatesRequest || (templatesRequest = fetchTemplates(templateGroup))
            .catch(function (error) {
              console.log('ERROR caught');
              templatesRequest = undefined;
              return $q.reject(error);
            });
        }
      }

      let templateRequestPromises = {};

      var fetchTemplates = function (templateGroup) {
        return ApplicationConfigurationService.getServiceConfigurationFor ('TemplateService')
          .then (function (configuration) {

            var alias;
            if(configuration.templateListQuickCreatorAlias
              && templateGroup==='QuickCreator'){
              alias = configuration.templateListQuickCreatorAlias
                || 'aceUI/defaultTemplateList';
            } else {
              alias = configuration.templateListAlias || 'aceUI/defaultTemplateList';
            }
            // Get content of type aceUITemplateList
            return ContentService.getContent ({ alias , view: 'aceLatest' })
              .catch (function (error) {
                // Something went wrong when getting the template list content
                console.log('ERROR: could not get content');
                var aceError = AceUIError.Error ({
                  origin: 'TemplateService',
                  severity: AceUIError.severities.CRITICAL,
                  rootCause: error
                });

                if (error.rootCause.status === 404) {
                  if (configuration.templateListAlias || configuration.templateListCreateAlias) {
                    aceError.cause = 'Could not find templateList content "' + alias +  '" configured in the ' +
                      'application configuration.';
                  } else {
                    aceError.cause = 'No templateList content was configured in the application configuration and ' +
                      'no default templateList ("' + alias +  '") content was found in the system.';
                  }
                  aceError.type = AceUIError.types.NOT_FOUND;
                } else {
                  aceError.cause = 'Could not fetch the template list';
                  aceError.type = AceUIError.types.REQUEST_FAILED;
                }

                return $q.reject (aceError);
              })
              .then (function (result) {
                var templateList = atex.aceui.ObjectUtils.getByPath (result, 'content/aspects/aceUITemplateList/templateList') || [];

                // Get the templates
                return $q.all (templateList.map (function (alias) {
                  return templateRequestPromises[alias] = templateRequestPromises[alias] || FileDeliveryService.getTextFile ({ alias, fileName: 'template.json' })
                    .then (function (fileResult) {
                      let template = JSON.parse (fileResult.text);
                      template.id = alias.split('/')[1];

                      return template;
                    });
                }))
                  .catch (function (error) {
                    // Something went wrong while fetching or parsing templates
                    return $q.reject (AceUIError.Error ({
                      origin: 'TemplateService',
                      type: AceUIError.types.REQUEST_FAILED,
                      severity: AceUIError.severities.CRITICAL,
                      cause: 'Could not read one or more templates from the server.',
                      rootCause: error
                    }));
                  });

              });
          });
      };

      var filterOnPermission = function (templates, permission, securityContext) {
        return $q.all (templates.map((template) => {
          return AuthorizationService.hasPermission (permission, template.type, securityContext)
            .then (function (hasPermission) {
              return hasPermission;
            });
        }))
          .then (function (hasPermissions) {
            return templates.filter (function (template, i) {
              return hasPermissions[i];
            });
          });
      };

      return {
        /**
         * Discards all cached templates, if any.
         */
        reset: function () {
          templatesRequest = undefined;
        },

        /**
         * Get templates for Quick Creator menu dropdown for which the current user has the specific permission.
         *
         * @param permission
         * @param [type] Get only templates for a specific type
         *
         * @return Promise<Array> The resolved Array is a list of templates found (empty if not found)
         *                        Rejects with an error if something went wrong while fetching.
         *
         */
        getQuickCreatorTemplates: function (permission, type) {
          return fetchAndCacheTemplates ('QuickCreator')
            .then (function (templates) {
              return filterOnPermission (templates, permission);
            })
            .then (function (templates) {
              return type ? templates.filter((t) => t.type === type) : templates;
            })
            .then (function (templates) {
              return templates || [];
            });
        },

        /**
         * Get templates for types for which the current user has the specific permission.
         *
         * @param permission
         * @param [type] Get only templates for a specific type
         *
         * @return Promise<Array> The resolved Array is a list of templates found (empty if not found)
         *                        Rejects with an error if something went wrong while fetching.
         *
         */
        getTemplates: function (permission, type, securityContext) {
          return fetchAndCacheTemplates ()
            .then (function (templates) {
              return filterOnPermission (templates, permission, securityContext);
            })
            .then (function (templates) {
              return type ? templates.filter((t) => t.type === type) : templates;
            })
            .then (function (templates) {
              return templates || [];
            });
        },

        /**
         * Get a single template from the available templates in this TemplateService.
         *
         * @param templateId An alias for a template
         * @param permission Permission to check for
         * @param securityContexts A list of security contexts for which the user has to have permission in.
         *
         * @return Promise<object> A promise which either resolves to a template or is rejected with an Error object.
         * If the template was not found the promise resolves to undefined.
         */
        getTemplate: function (templateId, permission, securityContexts) {
          return fetchAndCacheTemplates ()
            .then (function (templates) {
              return filterOnPermission (templates, permission, securityContexts);
            })
            .then (function (templates) {
              return templates.find((t) => t.id === templateId);
            });
        }
      };
    }
  ]
);
